import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { appSettings } from 'app/appSettings';
import { SessionState } from 'app/core/store/session/session.interface';
import { logger } from 'app/core/util/logger';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { distinctUntilChanged, filter, first, map, takeWhile } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../../core/authentication/authentication.service';
import { AutoLogoutService } from '../../core/authentication/auto-logout.service';
import { ConfigService } from '../../core/config/config-service';
import { MockService } from '../../core/services/mock.service';
import { UserContextService } from '../../core/services/user-context.service';
import { GetConfigAction } from '../../core/store/config/config.actions';
import { ContractRestrictions } from '../../core/store/contract-details/contract-details.interface';
import { Definition } from '../../core/store/definitions/definition.interface';
import { DefinitionsService } from '../../core/store/definitions/definitions.service';
import { goAction } from '../../core/store/router/router.actions';
import { ClearSelectedCustomerAction } from '../../core/store/session/selected-customer/select-customer.actions';
import { BaseContainer } from '../../modules/base/BaseContainer';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends BaseContainer implements OnInit, OnDestroy {
  private authenticationService = inject(AuthenticationService);

  isIframe = false;

  firstName: string;
  initials: string;
  selectedCustomer: Definition;
  contextLoading: boolean = false;
  authorized: boolean = undefined;
  customerInfoLoaded: boolean = false;
  canSwitchCustomer: boolean = false;
  loading: boolean = false;
  path: string;
  contractRestrictions: ContractRestrictions;
  isEpsUser: boolean = false;
  autologoutDisabled: boolean = false;
  isSuperUser: boolean = false;

  constructor(
    translate: TranslateService,
    config: ConfigService,
    private autoLogService: AutoLogoutService,
    definitionService: DefinitionsService,
    ccService: NgcCookieConsentService,
    private userContextService: UserContextService,
    mockService: MockService
  ) {
    super();
    if (environment.mocksEnabled) {
      mockService.activate();
    }

    // Set language
    const setLanguage = function (lang?: string) {
      const language = (lang && lang.toLowerCase()) || appSettings.DEFAULT_LANGUAGE;
      translate.setDefaultLang(appSettings.DEFAULT_LANGUAGE);
      translate.use(language);
      appSettings.LANGUAGE = language;
    };
    setLanguage();

    this.store.dispatch(new GetConfigAction(config.getConfig()));

    this.authenticationService.authenticated$
      .pipe(
        filter(authenticated => authenticated),
        first()
      )
      .subscribe(() => {
        this.userContextService.loadOnce();
      });

    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.customerInfo),
        distinctUntilChanged()
      )
      .subscribe(customerInfo => {
        this.customerInfoLoaded = customerInfo && customerInfo.loaded;
        this.contractRestrictions = customerInfo && customerInfo.restrictions;
      });

    this.store
      .pipe(
        map(state => state.ui),
        distinctUntilChanged()
      )
      .subscribe(ui => {
        this.loading = ui.busy;
        logger.debug(() => `loading: ${this.loading}`);
        this.cd.markForCheck();
      });

    this.store
      .select('session')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((session: SessionState) => {
        const userContext = session.userContext;
        if (userContext) {
          if (userContext.contextLoaded) {
            setLanguage(userContext.language);
            definitionService.loadOnce(userContext.language);
            this.firstName = userContext.firstName;
            this.initials = userContext.initials;
            this.contextLoading = userContext.contextLoading;
            this.authorized = session.userContext.authorized;
            this.isEpsUser = userContext.isEpsUser;
            this.canSwitchCustomer = this.isEpsUser || (userContext.customers && userContext.customers.length > 1);
            this.selectedCustomer = session.selectedCustomer.data;
            this.isSuperUser = session && session.userContext && session.userContext.isSuperUser;
          }
        }
      });

    this.store
      .select('router')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe(router => {
        this.path = router && router.state.url;
      });

    translate.get(['COOKIE.HEADER', 'COOKIE.MESSAGE', 'COOKIE.DISMISS', 'COOKIE.ALLOW', 'COOKIE.DENY', 'COOKIE.LINK', 'COOKIE.POLICY']).subscribe(data => {
      ccService.getConfig().content = ccService.getConfig().content || {};
      // Override default messages with the translated ones
      ccService.getConfig().content.header = data['COOKIE.HEADER'];
      ccService.getConfig().content.message = data['COOKIE.MESSAGE'];
      ccService.getConfig().content.dismiss = data['COOKIE.DISMISS'];
      ccService.getConfig().content.allow = data['COOKIE.ALLOW'];
      ccService.getConfig().content.deny = data['COOKIE.DENY'];
      ccService.getConfig().content.link = data['COOKIE.LINK'];
      ccService.getConfig().autoOpen = true;

      ccService.destroy(); // remove previous cookie bar (with default messages)
      ccService.init(ccService.getConfig()); // update config with translated messages
    });
  }

  login(): void {
    if (this.authorized === false) {
      this.authenticationService.logout();
    }
    this.authenticationService.login();
  }

  logout(): void {
    this.authenticationService.logout();
  }

  selectCustomer(): void {
    this.store.dispatch(new ClearSelectedCustomerAction());
    this.store.dispatch(goAction({ path: appSettings.SELECT_CUSTOMER_ROUTE }));
  }

  openModule(path: string): void {
    this.store.dispatch(goAction({ path }));
  }

  gotoDashboard(): void {
    this.store.dispatch(goAction({ path: '/' }));
  }

  ngOnInit() {
    this.setAutologout();
    this.isIframe = window !== window.parent && !window.opener;
  }

  toggleAutologout() {
    localStorage.setItem('autologoutDisabled', this.autologoutDisabled ? 'false' : 'true');
    this.setAutologout();
  }

  private setAutologout() {
    try {
      this.autologoutDisabled = localStorage.getItem('autologoutDisabled') === 'true';
      if (this.autologoutDisabled) {
        this.autoLogService.disable();
      } else {
        this.autoLogService.enable();
      }
    } catch (e) {
      logger.error('Issue with localStorage.getItem()');
    }
  }

  ngOnDestroy() {
    this.autoLogService.destroy();
    super.ngOnDestroy();
  }
}
